import { useNavigate, useParams } from 'react-router-dom';
import { WithOrderSummaryLayout } from '../../../components/WithOrderSummaryLayout';
import { Heading2 } from '../../../components/shared/Heading';
import { OrderSummary } from '../../OrderSummary';
import { ServiceList } from '../../Services/ServiceList';
import { useEffect, useState } from 'react';
import { RateMode } from '../../../stores/paramsStore';
import { useRootStore } from '../../../stores/useRootStore';
import { observer } from 'mobx-react-lite';
import { ServiceStore } from '../../../stores/serviceStore';
import { BookingInfo } from '../../../apiMethods/getBookingInfo';
import { Button } from '../../../components/shared/Button/Button';
import { addServices } from '../../../apiMethods/addServices';
import { NotFoundPlug } from '../../../components/Plug/NotFoundPlug';
import ArrowWithTailIcon from '../../../assets/arrow-tail.svg?react';
import { parseDate } from '../../../utils/dates';
import { BackLink } from '../../../components/shared/BackButton/BackLink';

const useRange = (arrival?: string, departure?: string) => {
  const from = arrival ? parseDate(arrival) : undefined;
  const to = departure ? parseDate(departure) : undefined;
  return { from, to };
};

const useBookingSummaryParams = (bookingInfo: BookingInfo | null, servicesTotal: number) => {
  const totalPrice = Number(bookingInfo?.price) ?? 0 + servicesTotal;

  const range = useRange(bookingInfo?.arrival, bookingInfo?.departure);

  const params = {
    from: range.from,
    to: range.to,
    adults: bookingInfo?.adults ?? 0,
    childs: bookingInfo?.childs.reduce((count, x) => x.quantity + count, 0) ?? 0,
    rateName: bookingInfo?.rate.name,
    roomName: bookingInfo?.room_type.name,
    roomsCount: bookingInfo?.rooms_count,
    totalPrice,
  };

  return { params, range };
};

export const AddServices = observer(() => {
  const navigate = useNavigate();
  const { bookingId } = useParams<{ bookingId: string }>();
  const { configStore, bookingsStore } = useRootStore();
  const hotelId = configStore.hotelId;

  const [serviceStore] = useState(() => new ServiceStore());

  useEffect(() => {
    bookingId && bookingsStore.loadBookingInfo(bookingId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  useEffect(() => {
    if (!hotelId || !bookingsStore.bookingInfo) {
      return;
    }

    const { arrival, departure, rate_flow, rooms_filter } = bookingsStore.bookingInfo;

    // validateRateFlow
    const validRateFlows: RateMode[] = ['CORP', 'SPEC', 'OPEN'];
    if (!validRateFlows.includes(rate_flow as RateMode)) {
      throw new Error(`"${rate_flow}" is not a type of rate_flow'`);
    }

    const bodyRequest = {
      arrival,
      departure,
      rooms: rooms_filter,
      promo_code: '',
      rate_flow: (rate_flow || 'OPEN') as RateMode,
    };

    serviceStore.fetchServices(hotelId, bodyRequest);
  }, [hotelId, bookingsStore.bookingInfo, serviceStore]);

  const { params, range } = useBookingSummaryParams(
    bookingsStore.bookingInfo,
    serviceStore.getTotalPrice(),
  );

  const [pending, setPending] = useState(false);

  const onSubmit = async () => {
    const hotelId = configStore.hotelId;
    if (!hotelId || !bookingId) {
      return;
    }

    const params = {
      reservation_id: Number(bookingId),
      services: serviceStore.selectedServices.map(x => ({
        price: x.price,
        quantity: x.quantity,
        service_id: x.serviceId,
        sub_service_id: x.subServiceId,
        comment: x.comment ?? '',
      })),
    };

    try {
      setPending(true);
      const response = await addServices(hotelId, params);
      if (response?.payment_link) {
        window.location.replace(response.payment_link);
      }
    } finally {
      setPending(false);
    }
  };

  if (bookingsStore.bookingInfo?.read_only) {
    return (
      <NotFoundPlug title="Страница не доступна" text="Изменение бронирования не доступно">
        <Button onClick={() => navigate(-1)} className="mx-auto flex items-center gap-2 px-4 py-3 ">
          <ArrowWithTailIcon /> Вернуться к бронированию
        </Button>
      </NotFoundPlug>
    );
  }

  return (
    <WithOrderSummaryLayout
      showButton={serviceStore.selectedServices.length > 0}
      CustomButton={({ className }) => (
        <Button className={className} onClick={onSubmit} pending={pending}>
          Сохранить изменения
        </Button>
      )}
      sideContent={closeDialog => (
        <OrderSummary
          onClose={closeDialog}
          serviceStore={serviceStore}
          params={params}
          showServices
        />
      )}
    >
      <div>
        <BackLink to={`/booking-list/${bookingId}`}>К бронированию</BackLink>
        <Heading2 className="mt-8">Дополнительные услуги</Heading2>

        <ServiceList serviceStore={serviceStore} params={range} />
      </div>
    </WithOrderSummaryLayout>
  );
});
